import { useEffect, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import type { components } from '../../api/generated/schema'
// This file's own throws only. It is deliberately NOT re-exported: the class
// lives in `householdError.ts` because that module imports nothing, and
// leaving a second door open here invites the next author to import it from
// the module that pulls in `api/client` — which is the import-time dependency
// the extraction existed to remove (web-architect gate, finding on #359).
// One door.
import { HouseholdError } from './householdError'

export type Household = components['schemas']['HouseholdDetailResponse']
type HouseholdBrief = components['schemas']['HouseholdResponse']


/**
 * D22 puts theme and layout in `localStorage`; the resolved household id joins
 * them, for the same reason and with the same caveat — it is a client-side
 * preference with no backend field to hold it.
 */
export const HOUSEHOLD_ID_STORAGE_KEY = 'tracke5.householdId'

async function listHouseholds(): Promise<HouseholdBrief[]> {
  const { data, error, response } = await api.GET('/api/v1/households')
  if (error !== undefined || data === undefined) throw new HouseholdError(error, response.status)
  return data.households
}

async function getHousehold(id: string): Promise<Household> {
  const { data, error, response } = await api.GET('/api/v1/households/{household_id}', {
    params: { path: { household_id: id } },
  })
  if (error !== undefined || data === undefined) throw new HouseholdError(error, response.status)
  return data
}

/**
 * `VerifyDevice.tsx`'s split, the same shape here: 4xx is about the
 * CREDENTIAL — a viewer's 403 (issue 321) repeats identically on every
 * retry, so retrying it just delays the report by a round trip. 5xx and 429
 * are about the SERVER, and the household is still there, so those keep the
 * app's normal one retry. A plain network failure (no `HouseholdError`, no
 * status) also keeps the normal retry — it isn't the credential problem this
 * predicate exists to short-circuit.
 */
function isRetryableHouseholdError(error: unknown): boolean {
  return !(error instanceof HouseholdError && error.status < 500 && error.status !== 429)
}

/**
 * Which household this app is looking at.
 *
 * **This is not `households[0]`, and that is the whole point of the hook.**
 * `api/dependencies.py:270` creates a personal household for every user at
 * signup — `"{display_name}'s Household"`, with them as OWNER — unless they
 * have a pending invitation. `accept_invitation` does NOT replace it. So the
 * normal outcome of inviting someone who already has an account is that they
 * belong to two households, and `list_user_households` orders
 * `created_at DESC`, which usually puts their own empty one first. D12 says
 * "the first household is implicit"; taken literally, an invited user opens
 * the app to nothing.
 *
 * The rule, settled at intake:
 *
 * 1. One household — bind to it, and never fetch a detail.
 * 2. A persisted id still present in the list — bind to it.
 * 3. Otherwise prefer a household with `member_count > 1` (the shared one).
 * 4. Tie or none — the OLDEST by `created_at`, which is the one that existed
 *    before the personal household was manufactured.
 *
 * Step 3 is the only one that costs requests, and only when the user is in
 * more than one household, which is at most a handful.
 *
 * **This is not a switcher.** D12 still holds: there is no UI to change the
 * binding. The persistence exists so the resolution is stable across reloads,
 * not so it can be chosen.
 *
 * **The viewer 403 (issue 321).** `GET /households/{id}` requires member or
 * higher, so a viewer's detail calls all 403 rather than resolve. That is not
 * a transient failure to retry and must not fall through to "no household" —
 * `isForbidden` is true when every detail call comes back 403, and Task 4
 * renders it as its own message rather than a generic error.
 *
 * **Only steps 3 and 4 write.** Step 1 (`households.length === 1`) never
 * chose between anything — there was nothing to choose — so persisting it is
 * what turns it into damage: a user with no invitation yet gets `remember`d
 * onto their empty personal household on visit 1, and step 2 then finds that
 * write "valid" (it's still in the list) on every later visit, including the
 * one right after they accept an invitation. `settled` goes true, the detail
 * queries that would have found the shared household never fire, and they
 * are stuck on the empty one — forever, since D12 gives them no switcher.
 * Only steps 3 and 4 resolved anything, so only they persist, and only when
 * every detail call that informed the decision actually succeeded — a
 * decision partly made of gaps (issue 321, or a plain 500) must not be
 * written down as if it were complete.
 *
 * **Resolves purely; persists in an effect.** `readStoredId` runs once via
 * `useState`'s initialiser, not on every render — `RequireAuth.tsx`'s
 * `useReturnTo` header has the same shape: a render-body read paired with a
 * render-body write of the *same* store made the render impure, and
 * `<StrictMode>`'s double-invoke (`main.tsx` mounts under it) threw away
 * what the first pass had just taken. Here the concrete failure was
 * `settled` — and so `enabled` on the detail queries — being computed from a
 * value this same component had written earlier in the same render.
 * `ThemeProvider`/`ChromeLayout` are the established shape: read once into
 * state, write from an effect (or a handler) after the store is
 * side-effect-free to touch.
 */
export function useHouseholdId(): {
  id: string | undefined
  isPending: boolean
  isError: boolean
  /** Only meaningful when `isError` is true — which request failed, so
   *  `Household.tsx` can offer a reload for a transient list failure and
   *  withhold it for a detail failure a reload cannot fix (issue 311). */
  errorCause: 'list' | 'detail' | undefined
  isForbidden: boolean
} {
  const list = useQuery({ queryKey: keys.household.list(), queryFn: listHouseholds })
  const households = list.data ?? []

  const [stored] = useState(readStoredId)
  const storedIsValid = stored !== null && households.some((h) => h.id === stored)
  /* Resolution is settled without any detail call in the two common cases, so
     the queries below stay disabled rather than firing and being ignored. */
  const settled = households.length <= 1 || storedIsValid

  const details = useQueries({
    queries: households.map((h) => ({
      queryKey: keys.household.detail(h.id),
      queryFn: () => getHousehold(h.id),
      enabled: !settled,
      retry: (count: number, error: unknown) => isRetryableHouseholdError(error) && count < 1,
    })),
  })

  const resolved = resolveHousehold(list, households, stored, storedIsValid, details)

  const toPersist = resolved.status === 'resolved' && resolved.persist ? resolved.id : undefined
  useEffect(() => {
    if (toPersist !== undefined) writeHouseholdId(toPersist)
  }, [toPersist])

  return {
    id: resolved.status === 'resolved' ? resolved.id : undefined,
    isPending: resolved.status === 'pending',
    isError: resolved.status === 'error',
    errorCause: resolved.status === 'error' ? resolved.cause : undefined,
    isForbidden: resolved.status === 'forbidden',
  }
}

type DetailResult = {
  isPending: boolean
  isError: boolean
  error: unknown
  data: Household | undefined
}

/**
 * Four outcomes carry nothing but their name; the fifth carries what it
 * resolved to. A set of independent booleans let a caller build a
 * combination none of the five rules produce — `EMPTY` used to be
 * indistinguishable from "still resolving" by its shape (`id: undefined`,
 * every flag `false`), recoverable only by knowing the check order. The
 * union makes that combination unrepresentable instead.
 */
/**
 * `error` carries WHICH request failed, not just that one did — issue 311
 * (M12 gate, blocking finding 3). The two call sites below used to collapse
 * onto the same `{ status: 'error' }`, and `Household.tsx` rendered both as
 * "Household could not be loaded. / Reload the page." That copy is TRUE for
 * `'list'` — the household LIST request itself failed, a plain 500 the docblock
 * already described — and FALSE for `'detail'`: reload cannot fix a 403 that
 * repeats deterministically (`isRetryableHouseholdError` correctly refuses to
 * retry a 4xx), so a common viewer stuck on a household where every OTHER
 * detail call also failed was offered a recovery that can never recover, on
 * the same population issue 321 is about.
 */
type Resolution =
  | { status: 'pending' }
  | { status: 'error'; cause: 'list' | 'detail' }
  | { status: 'forbidden' }
  | { status: 'none' }
  | { status: 'resolved'; id: string; persist: boolean }

/**
 * Pure — no read or write of `localStorage`, and no hook. `stored` and
 * `details` are already-resolved inputs the caller collected from hooks;
 * this function only decides what they mean. Kept separate from
 * `useHouseholdId` so the branching above (which id, and whether it earned a
 * write) can be exercised without a `renderHook` for every case, and so the
 * hook body has no path that computes an id without also deciding `persist`
 * for it.
 */
function resolveHousehold(
  list: { isError: boolean; isPending: boolean },
  households: HouseholdBrief[],
  stored: string | null,
  storedIsValid: boolean,
  details: readonly DetailResult[],
): Resolution {
  if (list.isError) return { status: 'error', cause: 'list' }
  if (list.isPending) return { status: 'pending' }

  if (households.length === 0) return { status: 'none' }
  // `households[0]!` — the length check just above is the guarantee. Never
  // persisted: this branch chose between nothing, so writing it is what
  // makes step 2 wrongly "valid" forever once a second household arrives.
  if (households.length === 1) return { status: 'resolved', id: households[0]!.id, persist: false }
  // Already on disk from an earlier resolution — re-writing it is a no-op.
  if (storedIsValid) return { status: 'resolved', id: stored as string, persist: false }

  if (details.some((d) => d.isPending)) return { status: 'pending' }

  const allForbidden = details.length > 0 && details.every((d) => d.error instanceof HouseholdError && d.error.status === 403)
  if (allForbidden) return { status: 'forbidden' }

  /* A detail call failing for any reason other than a uniform 403 is an
     error, not a fallback. Filtering failed calls out below and picking from
     whatever data did arrive would silently bind to whichever household
     happened to answer — the exact failure this hook exists to prevent,
     reappearing one layer down. */
  const anyDetailFailed = details.some((d) => d.isError)
  if (anyDetailFailed) return { status: 'error', cause: 'detail' }

  const shared = details
    .map((d) => d.data)
    .filter((d): d is Household => d !== undefined)
    .filter((d) => d.member_count > 1)

  const id = shared.length > 0 ? oldest(shared).id : oldest(households).id
  /* Every call above this point has already returned on the first `isPending`
     or `isError`, so every remaining `d.data` is defined — this is not the
     guard that makes the write safe, `anyDetailFailed` above is. It's the
     visible statement of the invariant `anyDetailFailed` establishes, so a
     change to either one has to keep them in agreement rather than trusting
     the other silently. */
  const persist = details.every((d) => d.data !== undefined)
  return { status: 'resolved', id, persist }
}

function oldest<T extends { created_at: string }>(rows: readonly T[]): T {
  // No initial value on the reduce below would already throw on an empty
  // array rather than quietly returning `undefined`, but with a message
  // naming neither this function nor the empty households — an explicit
  // guard says what a future call site broke: it skipped the
  // non-emptiness check `oldest` relies on (`shared.length > 0`, or
  // `households` once the length-0 and length-1 branches have already
  // returned).
  if (rows.length === 0) throw new Error('oldest(): received an empty array; the caller must guarantee at least one row')
  return rows.reduce((a, b) => (a.created_at <= b.created_at ? a : b))
}

function readStoredId(): string | null {
  try {
    return localStorage.getItem(HOUSEHOLD_ID_STORAGE_KEY)
  } catch {
    /* Safari in private mode throws on access, not just on write. A binding
       that cannot be remembered is still a binding — it just re-resolves. */
    return null
  }
}

/** Exported for `AcceptInvitation.tsx`, whose accept response carries the
 *  joined household's id directly — the same store, the same private-mode
 *  caveat, so it belongs to the module that owns the key rather than being
 *  re-implemented at the call site. */
export function writeHouseholdId(id: string): void {
  try {
    localStorage.setItem(HOUSEHOLD_ID_STORAGE_KEY, id)
  } catch {
    /* See above. Losing the write costs a detail round-trip next reload. */
  }
}

/**
 * Exported for `useLeaveHousehold` (Task 7, tracke5-webapp#314) — the stored
 * id names a household the viewer has just left, and clearing it here rather
 * than a hand-rolled `localStorage.removeItem` at the call site is the same
 * argument `writeHouseholdId`'s own docblock makes: this module owns the
 * key, so the private-mode try/catch is written once (web-architect review,
 * issue 314 fix round, N4).
 */
export function clearHouseholdId(): void {
  try {
    localStorage.removeItem(HOUSEHOLD_ID_STORAGE_KEY)
  } catch {
    /* Private mode. The stored id is then already unreadable. */
  }
}

/**
 * The household's own record — name and `member_count`, for the screen header.
 *
 * Not routed through `<Async>` by the screen: the members list is the surface
 * that owns the loading and error treatment, and two `<Async>` wrappers stacked
 * on one panel would show two skeletons for one screen.
 */
export function useHousehold(id: string | undefined): AsyncState<Household> {
  const query = useQuery({
    queryKey: keys.household.detail(id ?? ''),
    queryFn: () => getHousehold(id as string),
    enabled: id !== undefined,
  })
  return fromQuery(query, {
    message: () => 'This household could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}

/**
 * The household a merchant can be shared with, or `null` — for "Share with household" (#495).
 *
 * **Not just `useHouseholdId().id`.** The backend makes a personal household for every user at signup
 * (`useHouseholdId`'s docblock), so "has a household" is always true and says nothing. Sharing into a
 * household of one shares with no one, so the option is offered only when the bound household has
 * another member (`member_count > 1`), read from its detail. Anything unread — loading, a viewer's 403,
 * a failure — is `null`, and the option stays hidden: it is never offered on a guess.
 */
export function useSharedHousehold(): string | null {
  const { id } = useHouseholdId()
  const detail = useHousehold(id)
  if (id === undefined || (detail.status !== 'success' && detail.status !== 'stale')) return null
  return detail.data.member_count > 1 ? detail.data.id : null
}
